/**
 * The three depths an anchorage can have, and the invitation to add the third.
 *
 * They are different claims and are never merged:
 *
 *   depth_notes     what ABG know about the bay, in prose
 *   chart_depth     Kartverket's soundings -- how deep the bay is
 *   reported_depth  what captains found -- how deep it was where a boat lay
 *
 * The last is worth most to somebody choosing a berth, and the only people who
 * can supply it are the ones who have lain there. Hence an invitation rather
 * than an empty field: a captain who has just swung on 22 m knows something the
 * chart does not.
 *
 * Never a single number and never an average. The charted spread inside 150 m
 * reaches 3.6-61.0 m at one of ABG's own anchorages, and an average would hide
 * that two readings disagree -- which is the useful signal.
 */

import { useState } from 'react'
import { Field, Notice } from '@abg/ui'
import { api, ApiError } from '../lib/api'
import type { Anchorage, ChartDepth, ReportedDepth } from '../lib/types'

function range(min: number, max: number): string {
  return Math.abs(max - min) < 0.05
    ? `${min.toFixed(1)} m`
    : `${min.toFixed(1)} \u2013 ${max.toFixed(1)} m`
}

function Charted({ depth }: { depth: ChartDepth }) {
  if (depth.count === 0 || depth.min_m === null || depth.max_m === null) {
    return (
      <li>
        <span>Charted</span>
        <span className="muted">not sounded within {Math.round(depth.radius_m ?? 0)} m</span>
      </li>
    )
  }
  return (
    <li>
      <span>Charted</span>
      <span className="mono">
        {range(depth.min_m, depth.max_m)}
        <span className="caption">
          {" \u00b7 "}
          {depth.count} sounding{depth.count === 1 ? '' : 's'} within{' '}
          {Math.round(depth.radius_m ?? 0)} m
        </span>
      </span>
    </li>
  )
}

export function DepthPanel({
  anchorage,
  onReported,
}: {
  anchorage: Anchorage
  onReported: (reported: ReportedDepth) => void
}) {
  const [open, setOpen] = useState(false)
  const [low, setLow] = useState('')
  const [high, setHigh] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [thanks, setThanks] = useState(false)

  const submit = async () => {
    const min = Number(low.replace(',', '.'))
    // A single reading is a range of zero width. Leaving the second field
    // empty is the common case and must not be an error.
    const max = high.trim() === '' ? min : Number(high.replace(',', '.'))
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      setError('Give the depth in metres — 22, or 20 and 24 for a range.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const reported = await api.post<ReportedDepth>(
        `/api/anchorages/${anchorage.id}/depth-reports`,
        { min_m: min, max_m: max, note: note.trim() || null },
      )
      onReported(reported)
      setThanks(true)
      setOpen(false)
      setLow('')
      setHigh('')
      setNote('')
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not send that.')
    } finally {
      setBusy(false)
    }
  }

  const reported = anchorage.reported_depth

  return (
    <div className="stack-sm">
      <div className="eyebrow">Depth</div>
      <ul className="usage-list">
        {anchorage.chart_depth && <Charted depth={anchorage.chart_depth} />}
        <li>
          <span>Captains report</span>
          {reported ? (
            <span className="mono">
              {range(reported.min_m, reported.max_m)}
              <span className="caption">
                {" \u00b7 "}
                {reported.count} report{reported.count === 1 ? '' : 's'}
              </span>
            </span>
          ) : (
            <span className="muted">nobody yet</span>
          )}
        </li>
      </ul>

      {/* The shallowest charted reading is the one that grounds a yacht, so it
          is called out rather than left to be inferred from a range. */}
      {anchorage.chart_depth?.min_m != null && anchorage.chart_depth.min_m < 10 && (
        <div className="notice notice-warn">
          <strong>
            Shallowest charted sounding here is {anchorage.chart_depth.min_m.toFixed(1)} m.
          </strong>{' '}
          Check where it lies before you swing.
        </div>
      )}

      {thanks && <Notice>Thank you — that goes to the next captain who asks.</Notice>}

      {!open ? (
        <button type="button" className="btn btn-quiet btn-sm" onClick={() => setOpen(true)}>
          {reported ? 'Add what you found' : 'Tell us the depth you found'}
        </button>
      ) : (
        <div className="card stack-sm">
          <strong>What did you find?</strong>
          {error && <Notice tone="alert">{error}</Notice>}
          <div className="row">
            <Field label="Depth (m)">
              <input
                type="text"
                inputMode="decimal"
                value={low}
                onChange={(e) => setLow(e.target.value)}
                placeholder="22"
              />
            </Field>
            <Field label="To (optional)" hint="If it varied where you swung.">
              <input
                type="text"
                inputMode="decimal"
                value={high}
                onChange={(e) => setHigh(e.target.value)}
                placeholder="26"
              />
            </Field>
          </div>
          <Field
            label="Anything else?"
            hint="Holding, obstructions — whatever you would tell the next master."
          >
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Mud, held well in 25 kn"
            />
          </Field>
          <div className="row">
            <button
              type="button"
              className="btn"
              disabled={busy || !low.trim()}
              onClick={submit}
            >
              {busy ? 'Sending…' : 'Send'}
            </button>
            <button
              type="button"
              className="btn btn-quiet"
              onClick={() => {
                setOpen(false)
                setError(null)
              }}
            >
              Cancel
            </button>
          </div>
          <p className="caption">
            Shown to other captains as a range and a count — never your vessel, and never
            when you were here.
          </p>
        </div>
      )}
    </div>
  )
}
